import React from 'react';
import { Modal, Progress, Typography, Space, Tag, Alert, Row, Col, Divider } from 'antd';
import { 
  LoadingOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined
} from '@ant-design/icons';
import useCampaignLaunchProgress from '../../../../core/hooks/useCampaignLaunchProgress';

const { Text, Title } = Typography;

const CampaignLaunchProgressModal = ({
  visible,
  onCancel,
  onCompleted,
  onFailed,
  onError,
  campaignOrderId,
  campaignName = 'Campaign',
  campaignStatus, // Add campaign status to detect completed state
  ...props
}) => {
  const {
    progressData,
    isActive,
    isLoading,
    hasError,
    error,
    phase,
    progress,
    message,
    step,
    current,
    total,
    metadata,
    elapsedTimeFormatted,
    estimatedRemainingTimeFormatted,
    isCompleted,
    isFailed,
    isPossibleTimeout
  } = useCampaignLaunchProgress(campaignOrderId, {
    enabled: visible,
    autoCleanup: false,
    onCompleted,
    onFailed,
    onError
  });

  // Detect if this is a completed campaign that should show final state
  const isCompletedCampaign = ['LIVE', 'PAUSED', 'LIVE_APPROVED'].includes(campaignStatus);
  const isFailedCampaign = campaignStatus === 'LAUNCHING_ERROR';

  // For completed campaigns, provide fallback data if no subscription data available
  const finalPhase = isCompletedCampaign ? 'COMPLETED' : isFailedCampaign ? 'FAILED' : phase;
  const finalProgress = isCompletedCampaign ? 100 : progress;
  const finalMessage = isCompletedCampaign 
    ? `Campaign launched successfully with ${campaignStatus === 'LIVE' ? 'LIVE' : 'PAUSED'} status.`
    : isFailedCampaign 
    ? 'Campaign launch failed. Please check the error details.'
    : message;
  const finalIsCompleted = isCompletedCampaign || isCompleted;
  const finalIsFailed = isFailedCampaign || isFailed;

  // Phase display configuration
  const getPhaseConfig = (currentPhase) => {
    const configs = {
      STARTING: { label: 'Starting', color: 'processing', icon: <LoadingOutlined /> },
      DATE_VALIDATION: { label: 'Date Validation', color: 'processing', icon: <LoadingOutlined /> },
      INSERTION_ORDER: { label: 'Insertion Order', color: 'processing', icon: <LoadingOutlined /> },
      PACKAGE: { label: 'Package Setup', color: 'processing', icon: <LoadingOutlined /> },
      LINE_ITEMS: { label: 'Line Items', color: 'processing', icon: <LoadingOutlined /> },
      CREATIVES: { label: 'Creatives', color: 'processing', icon: <LoadingOutlined /> },
      HEARTBEAT: { label: 'Processing', color: 'processing', icon: <ClockCircleOutlined /> },
      WARNING: { label: 'Warning', color: 'warning', icon: <ExclamationCircleOutlined /> },
      COMPLETED: { label: 'Completed', color: 'success', icon: <CheckCircleOutlined /> },
      FAILED: { label: 'Failed', color: 'error', icon: <CloseCircleOutlined /> }
    };
    return configs[currentPhase] || configs.STARTING;
  };

  const phaseConfig = getPhaseConfig(finalPhase);

  // Progress bar status
  const getProgressStatus = () => {
    if (finalIsFailed) return 'exception';
    if (finalIsCompleted) return 'success';
    if (isPossibleTimeout) return 'exception';
    return 'active';
  };

  // Modal footer based on progress state
  const getModalFooter = () => {
    if (finalIsCompleted || finalIsFailed) {
      return [
        <button
          key="close"
          className="ant-btn ant-btn-primary"
          onClick={onCancel}
        >
          Close
        </button>
      ];
    }
    
    return [
      <button
        key="close"
        className="ant-btn"
        onClick={onCancel}
      >
        Close
      </button>
    ];
  };

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      title={
        <Space>
          {phaseConfig.icon}
          <span>
            {finalIsCompleted ? `Launch Complete: ${campaignName}` : 
             finalIsFailed ? `Launch Failed: ${campaignName}` : 
             `Launching ${campaignName}`}
          </span>
        </Space>
      }
      footer={getModalFooter()}
      maskClosable={false}
      closable={!isActive || finalIsCompleted || finalIsFailed}
      width={600}
      centered
      {...props}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        
        {/* Error Display */}
        {hasError && (
          <Alert
            message="Connection Error"
            description="Unable to connect to progress updates. Please check your connection."
            type="error"
            showIcon
          />
        )}

        {/* Timeout Warning */}
        {isPossibleTimeout && (
          <Alert
            message="Possible Timeout"
            description="Campaign launch is taking longer than expected. This may be due to campaign complexity or external factors."
            type="warning"
            showIcon
          />
        )}

        {/* Progress Bar */}
        <div>
          <Row justify="space-between" align="middle" style={{ marginBottom: 8 }}>
            <Col>
              <Space>
                <Tag color={phaseConfig.color} icon={phaseConfig.icon}>
                  {phaseConfig.label}
                </Tag>
                {metadata?.complexityLevel && (
                  <Tag color={metadata.complexityLevel === 'HIGH' ? 'red' : 
                            metadata.complexityLevel === 'MEDIUM' ? 'orange' : 'green'}>
                    {metadata.complexityLevel}
                  </Tag>
                )}
              </Space>
            </Col>
            <Col>
              <Text type="secondary">{finalProgress >= 0 ? `${finalProgress}%` : ''}</Text>
            </Col>
          </Row>
          
          <Progress
            percent={Math.max(0, finalProgress)}
            status={getProgressStatus()}
            strokeWidth={12}
            showInfo={false}
          />
        </div>

        {/* Current Message */}
        <div>
          <Title level={5} style={{ margin: 0 }}>
            {finalMessage || 'Initializing...'}
          </Title>
          {step && step !== finalMessage && (
            <Text type="secondary">{step}</Text>
          )}
        </div>

        {/* Progress Details */}
        {(current && total) && (
          <div>
            <Text>
              {metadata?.lineItemName && `Line Item: ${metadata.lineItemName} • `}
              {metadata?.creativeName && `Creative: ${metadata.creativeName} • `}
              Processing {current} of {total}
            </Text>
          </div>
        )}

        <Divider />

{/* TODO: Remove this once we have a way to display the time information */}
        {/* Time Information */}
        {/* <Row gutter={16}>
          <Col span={12}>
            <div>
              <Text strong>Elapsed Time</Text>
              <br />
              <Text type="secondary">
                {elapsedTimeFormatted || '0:00'}
              </Text>
            </div>
          </Col>
          <Col span={12}>
            <div>
              <Text strong>Estimated Remaining</Text>
              <br />
              <Text type="secondary">
                {estimatedRemainingTimeFormatted || 'Calculating...'}
              </Text>
            </div>
          </Col>
        </Row> */}

        {/* Campaign Details */}
        {metadata && (
          <>
            <Divider />
            <Row gutter={16}>
              {metadata.totalLineItems && (
                <Col span={12}>
                  <div>
                    <Text strong>Line Items</Text>
                    <br />
                    <Text type="secondary">{metadata.totalLineItems} total</Text>
                  </div>
                </Col>
              )}
              {metadata.totalCreatives && (
                <Col span={12}>
                  <div>
                    <Text strong>Creatives</Text>
                    <br />
                    <Text type="secondary">{metadata.totalCreatives} total</Text>
                  </div>
                </Col>
              )}
            </Row>
          </>
        )}

        {/* Error Details */}
        {isFailed && metadata?.errorDetails && (
          <>
            <Divider />
            <Alert
              message="Launch Failed"
              description={metadata.errorDetails}
              type="error"
              showIcon
            />
          </>
        )}

        {/* Success Message */}
        {isCompleted && (
          <>
            <Divider />
            <Alert
              message="Launch Completed"
              description="Your campaign has been successfully launched and is now running."
              type="success"
              showIcon
            />
          </>
        )}
        
      </Space>
    </Modal>
  );
};

export default CampaignLaunchProgressModal;